import { z } from 'zod';

import { ZodUserAccountType } from '@Common/types/questions/account_type';
import { ZodSuccessResponse } from '@Common/types/response';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

/**
 * POST /auth/reset_pin
 */

const ZodPostResetPinBody = z.object({
  email: ZodTrimmedString,
  signature: ZodTrimmedString,
  device_token: ZodTrimmedString,
  account_type: ZodUserAccountType.optional().default('personal'),
});

export type PostResetPinBody = z.infer<typeof ZodPostResetPinBody>;

const ZodPostResetPinResponse = z.object({
  ...ZodSuccessResponse.shape,
  new_code: z.boolean(),
  resend_at: z.string().datetime().optional(),
});

export type PostResetPinResponse = z.infer<typeof ZodPostResetPinResponse>;

/**
 * POST /auth/reset_pin/verify
 */

export const ZodPinCode = z.string().regex(/^\d+$/i).min(4).max(4);

const ZodPostResetPinVerifyBody = z.object({
  email: ZodTrimmedString,
  confirmation_code: ZodTrimmedString,
  signature: ZodTrimmedString,
  device_token: ZodTrimmedString,
  pin_code: ZodPinCode,
  account_type: ZodUserAccountType.optional().default('personal'),
});

export type PostResetPinVerifyBody = z.TypeOf<typeof ZodPostResetPinVerifyBody>;

export const ZodPostResetPinVerifyResponseData = z.object({
  token: ZodTrimmedString,
  refresh_token: ZodTrimmedString,
  display_name: ZodTrimmedString,
  device_token: ZodTrimmedString,
});
export type PostResetPinVerifyResponseData = z.infer<typeof ZodPostResetPinVerifyResponseData>;

const ZodPostResetPinVerifyResponse = z.object({
  success: z.boolean(),
  expired: z.boolean(),
  data: ZodPostResetPinVerifyResponseData.optional(),
});
export type PostResetPinVerifyResponse = z.infer<typeof ZodPostResetPinVerifyResponse>;
/**
 * POST /auth/reset_pin/verify_email
 */

const ZodPostResetPinVerifyEmailBody = z.object({
  email: ZodTrimmedString,
  signature: ZodTrimmedString,
  device_token: ZodTrimmedString.nullable(),
  phone_number: ZodTrimmedString,
  account_type: ZodUserAccountType.optional().default('personal'),
});

export type PostResetPinVerifyEmailBody = z.infer<typeof ZodPostResetPinVerifyEmailBody>;

/**
 * POST /auth/reset_pin/check
 */

const ZodPostResetPinCheckBody = z.object({
  email: ZodTrimmedString,
  confirmation_code: ZodTrimmedString,
  account_type: ZodUserAccountType.optional().default('personal'),
});

export type PostResetPinCheckBody = z.infer<typeof ZodPostResetPinCheckBody>;

const ZodPostResetPinCheckResponse = z.object({
  expired: z.boolean(),
  success: z.boolean(),
});
export type PostResetPinCheckResponse = z.infer<typeof ZodPostResetPinCheckResponse>;

export const postAuthResetPinEndpoint = endpoint({
  method: 'POST',
  body: ZodPostResetPinBody,
  responseBody: ZodPostResetPinResponse,
});

export type PostAuthResetPinEndpoint = GetEndpointType<typeof postAuthResetPinEndpoint>;

export const postAuthResetPinCheckEndpoint = endpoint({
  method: 'POST',
  body: ZodPostResetPinCheckBody,
  responseBody: ZodPostResetPinCheckResponse,
});

export type PostAuthResetPinCheckEndpoint = GetEndpointType<typeof postAuthResetPinCheckEndpoint>;

export const postAuthResetPinVerifyEndpoint = endpoint({
  method: 'POST',
  body: ZodPostResetPinVerifyBody,
  responseBody: ZodPostResetPinVerifyResponse,
});

export type PostAuthResetPinVerifyEndpoint = GetEndpointType<typeof postAuthResetPinVerifyEndpoint>;

export const postAuthResetPinVerifyEmailEndpoint = endpoint({
  method: 'POST',
  body: ZodPostResetPinVerifyEmailBody,
  responseBody: ZodSuccessResponse,
});

export type PostAuthResetPinVerifyEmailEndpoint = GetEndpointType<
  typeof postAuthResetPinVerifyEmailEndpoint
>;
